/**
 * Push Notification Manager for HAL 3000
 * Handles service worker registration and push subscription
 */
import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { api } from '@/App';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationManager = ({ compact = false }) => {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      return;
    }
    setPermission(Notification.permission);
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      }
    } catch (e) {
      console.error('Check subscription error:', e);
    }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return;
      }

      const reg = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      const keyRes = await api.get('/push/vapid-public-key');
      const vapidPublicKey = keyRes.data.key;

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      await api.post('/push/subscribe', {
        subscription: subscription.toJSON(),
        device_name: navigator.userAgent.includes('iPad') ? 'iPad' :
                     navigator.userAgent.includes('iPhone') ? 'iPhone' : 'Navigateur',
      });

      setSubscribed(true);
    } catch (e) {
      console.error('Push subscribe error:', e);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.post('/push/unsubscribe', { subscription: sub.toJSON() });
          await sub.unsubscribe();
        }
      }
      setSubscribed(false);
    } catch (e) {
      console.error('Unsubscribe error:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;

  if (compact) {
    return (
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={loading}
        className={`p-2 rounded-lg border transition-all ${
          subscribed
            ? 'bg-green-900/30 border-green-500/30 text-green-400 hover:bg-green-900/50'
            : 'bg-gray-800 border-gray-600 text-gray-400 hover:bg-gray-700'
        }`}
        title={subscribed ? 'Notifications push activées - Cliquer pour désactiver' : 'Activer les notifications push'}
        data-testid="push-toggle-compact"
      >
        {loading ? <BellRing className="w-5 h-5 animate-pulse" /> :
         subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
      </button>
    );
  }

  return (
    <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-900/10" data-testid="push-notification-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-amber-400" />
          <div>
            <div className="text-lg font-mono font-bold text-amber-400">NOTIFICATIONS PUSH</div>
            <div className="text-xs font-mono text-gray-400">
              {!supported ? 'Non supporté par ce navigateur' :
               permission === 'denied' ? 'Bloqué - Autoriser dans les réglages du navigateur' :
               subscribed ? 'Activées - Vous recevrez des alertes même quand HAL est fermé' :
               'Désactivées - Activez pour recevoir des alertes en temps réel'}
            </div>
          </div>
        </div>
        <button
          onClick={subscribed ? unsubscribe : subscribe}
          disabled={loading || permission === 'denied'}
          className={`px-5 py-2.5 rounded-lg font-mono font-bold text-sm transition-all ${
            subscribed
              ? 'bg-red-900/30 border border-red-500/30 text-red-400 hover:bg-red-900/50'
              : 'bg-green-600 text-white hover:bg-green-700'
          } disabled:opacity-50`}
          data-testid="push-toggle-btn"
        >
          {loading ? 'En cours...' : subscribed ? 'DÉSACTIVER' : 'ACTIVER'}
        </button>
      </div>
    </div>
  );
};

export default PushNotificationManager;
